import type { UsePausableWatchers } from '@/utils/usePausableWatchers/types';
import type {
  WatchCallback,
  WatchOptions,
  WatchSource,
  WatchStopHandle,
} from 'vue';
import { nextTick, ref, watch } from 'vue';
import { generateUUID } from '@/utils/uuid/uuid';
import { MultiWatchSources } from '@vueuse/core';

const watchers = ref(new Map<string, WatchStopHandle>());
const isPaused = ref(false);

export const usePausableWatchers = (): UsePausableWatchers => {
  const register = (
    watcher: WatchSource | MultiWatchSources,
    watchCallback: WatchCallback,
    watchOptions?: WatchOptions,
  ): ReturnType<typeof generateUUID> => {
    const uuid = generateUUID();
    const unwatch = watch(
      watcher,
      (value, oldValue, onCleanup) => {
        if (isPaused.value) {
          return;
        }

        watchCallback(value, oldValue, onCleanup);
      },
      watchOptions,
    );

    watchers.value.set(uuid, unwatch);

    return uuid;
  };

  const unregister = (id: string): void => {
    const unwatch = watchers.value.get(id);
    if (unwatch !== undefined) {
      unwatch();

      watchers.value.delete(id);
    }
  };

  const pauseWhile = (callback: CallableFunction): void => {
    isPaused.value = true;
    callback();
    nextTick().then(() => {
      isPaused.value = false;
    });
  };

  return {
    register,
    unregister,
    pauseWhile,
  };
};
